import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { getPublicPlayerCard } from "../../services/PlayerCardService";
import PlayerCard from "../../components/athlete-scores/share-profile/player-card";
import LoadingSpinner from "../../components/LoadingSpinner";

const STATUS = {
  LOADING: "loading",
  CARD: "card",
  NO_CARD: "no-card",
  ERROR: "error",
};

/**
 * Public page for a shared player card link (/player-card/:token). Anyone can
 * open it without signing in. The backend returns only what the athlete chose
 * to show, or hasCard: false when the card was cleared, the token is unknown,
 * or (HS) the athlete's subscription has lapsed.
 */
export default function PublicPlayerCard() {
  const { token } = useParams();
  const [status, setStatus] = useState(STATUS.LOADING);
  const [card, setCard] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setStatus(STATUS.LOADING);

    getPublicPlayerCard(token)
      .then((data) => {
        if (cancelled) return;
        if (data?.hasCard && data?.card) {
          setCard(data.card);
          setStatus(STATUS.CARD);
        } else {
          setStatus(STATUS.NO_CARD);
        }
      })
      .catch(() => {
        if (!cancelled) setStatus(STATUS.ERROR);
      });

    return () => {
      cancelled = true;
    };
  }, [token]);

  if (status === STATUS.LOADING) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (status === STATUS.NO_CARD) {
    return (
      <MessagePage
        title="No player card to show"
        message="This athlete no longer has a player card. Please speak to the person who shared it with you."
      />
    );
  }

  if (status === STATUS.ERROR) {
    return (
      <MessagePage
        title="Something went wrong"
        message="We couldn't load this player card. Please try again later."
      />
    );
  }

  return (
    <div className="min-h-screen bg-[#363636]">
      <div className="max-w-5xl mx-auto px-4 py-6">
        <div className="mb-4 flex justify-center">
          <img
            src="/trackitt-logo.png"
            alt="Trackit Verified"
            className="h-12"
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        </div>
        <p className="py-6 text-center text-base font-bold uppercase text-gray-100">
          {card?.firstName} {card?.lastName} - player card
        </p>
        <div className="flex justify-center overflow-x-auto">
          <PlayerCard card={card} showTitle={false} />
        </div>
      </div>
    </div>
  );
}

function MessagePage({ title, message }) {
  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center px-4">
      <div className="bg-white rounded-xl shadow-md p-8 max-w-md w-full text-center">
        <h1 className="text-xl font-bold text-gray-800 mb-2">{title}</h1>
        <p className="text-gray-600 text-sm mb-6">{message}</p>
        <Link
          to="/"
          className="inline-block bg-blue-600 text-white px-6 py-2 rounded-md text-sm hover:bg-blue-700 transition"
        >
          Go to Home
        </Link>
      </div>
    </div>
  );
}
